import { ErrorText, Progress } from '@main/core-ui';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { downloadZipButtonMessages } from './messages';
import type { DownloadZipStatus } from './types';
import { ButtonWrapper, MessageWrapper } from './wrappers';

/**
 * DownloadRequest props
 */
export interface DownloadRequestProps extends DownloadZipStatus {
  /** Handler to perform zip */
  onClick: () => Promise<void>;
  /** Default behavior is to warn when someone is downloading a zip and then navigates away from the page  */
  warnOnNavigationAway?: boolean;
}

/**
 * A button for downloading the file
 */
export const DownloadZipButton: React.FC<DownloadRequestProps> = ({
  onClick,
  progress,
  isDownloading,
  message,
  warnOnNavigationAway = true,
}) => {
  const { formatMessage } = useIntl();

  // Holds the error message
  const [errorMessage, setErrorMessage] = useState('');

  // Warn before navigating away if mid-download
  useEffect(() => {
    window.onbeforeunload = () => {
      if (isDownloading && warnOnNavigationAway) {
        return true;
      }
      return undefined;
    };
  }, [isDownloading, warnOnNavigationAway]);

  // Render error
  if (errorMessage) {
    return <ErrorText>{errorMessage}</ErrorText>;
  }

  // Show loading indicator while downloading
  if (isDownloading) {
    return (
      <React.Fragment>
        <Progress value={progress} />
        <MessageWrapper>
          {formatMessage(message || downloadZipButtonMessages.saving)}
        </MessageWrapper>
      </React.Fragment>
    );
  }

  return (
    <>
      <ButtonWrapper
        buttonType="primary"
        onClick={async () => {
          try {
            await onClick();
          } catch (err) {
            setErrorMessage(err.message);
          }
        }}
        disabled={isDownloading}
      >
        {formatMessage(downloadZipButtonMessages.download)}
      </ButtonWrapper>
      {progress === 100 && (
        <>
          <br />
          <MessageWrapper>
            {formatMessage(downloadZipButtonMessages.downloaded)}
          </MessageWrapper>
        </>
      )}
    </>
  );
};
